import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { GroundCardLink } from "../../../../modules/content/components/GroundCardLink/GroundCardLink";
import { GroundCommonContent } from "../../../../modules/content/components/GroundCommonContent/GroundCommonContent";
import { GroundRichText } from "../../../../modules/content/components/GroundRichText/GroundRichText";
import { GroundSection } from "../../../../modules/content/components/GroundSection/GroundSection";
import type { IIconContentGridBlockProps } from "./IconContentGridBlock.props";

const columns = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
} as const;
const spans = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
} as const;

export const IconContentGridBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IIconContentGridBlockProps) => {
  if (!items?.length) return null;

  return (
    <GroundSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="flex flex-col gap-10 md:gap-14">
        <GroundCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div
          className={cn(
            "grid grid-cols-1 border-l border-t border-weathered-steel sm:grid-cols-2",
            columns[columnCount || 3],
          )}
        >
          {items.map((item, index) => (
            <GroundCardLink
              className={cn(
                "min-h-64 border-b border-r border-weathered-steel bg-background p-6",
                index === 0 &&
                  "bg-charcoal-green-ink text-mineral-gray hover:bg-basalt focus-visible:outline-oxidized-orange",
                spans[item.columnSpan || 1],
              )}
              key={index}
              link={item.cardLink}
            >
              <Icon
                className={cn("size-9 text-primary", index === 0 && "text-mineral-gray")}
                name={item.icon}
              />
              <h3
                className={cn(
                  "mt-14 font-ground-display text-2xl font-bold leading-tight text-foreground",
                  index === 0 && "text-mineral-gray",
                )}
              >
                {item.heading}
              </h3>
              {item.description ? (
                <GroundRichText
                  className={cn(
                    "mt-4 text-base text-basalt md:text-lg",
                    index === 0 &&
                      "text-mineral-gray [&_a]:text-mineral-gray [&_blockquote]:border-mineral-gray/40 [&_blockquote]:text-mineral-gray",
                  )}
                  content={item.description}
                />
              ) : null}
            </GroundCardLink>
          ))}
        </div>
      </div>
    </GroundSection>
  );
};

IconContentGridBlock.displayName = "Block::IconContentGridBlock";
